import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getArtworks, reset, fetchArtworkCategories } from '../features/artwork/artworkSlice';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import background from '../img/liiviiart-bg-home.jpeg';
import mobileBackground from '../img/logo-mobile.jpeg';
import PortfolioSection from '../components/PortfolioSection';
import { Link } from 'react-router-dom';

function Home() {
  const dispatch = useDispatch();
  const {
    artworks,
    isLoading,
    isError,
    message,
    categories,
    categoriesLoading,
    categoriesError,
    categoriesMessage,
  } = useSelector((state) => state.artwork);

  const artworksByCategory = useMemo(() => {
    const groups = new Map();
    if (!Array.isArray(artworks)) {
      return groups;
    }

    artworks.forEach((artwork) => {
      if (!artwork?.category) {
        return;
      }
      if (!groups.has(artwork.category)) {
        groups.set(artwork.category, []);
      }
      groups.get(artwork.category).push(artwork);
    });

    return groups;
  }, [artworks]);

  const homeCategories = useMemo(() => {
    if (!Array.isArray(categories)) {
      return [];
    }

    return categories
      .filter((category) => category.show_on_home)
      .sort((a, b) => {
        if (a.sort_order !== b.sort_order) {
          return a.sort_order - b.sort_order;
        }
        const nameA = a.display_name || a.category || '';
        const nameB = b.display_name || b.category || '';
        return nameA.localeCompare(nameB);
      });
  }, [categories]);

  const printCatalog = useMemo(() => {
    if (!Array.isArray(categories)) {
      return new Map();
    }

    const lookup = new Map();
    categories.forEach((category) => {
      const enabled =
        typeof category.print_enabled === 'boolean'
          ? category.print_enabled
          : Boolean(category.print_product_id);
      if (!enabled || !category.print_product_id) {
        return;
      }
      lookup.set(category.category, {
        productId: category.print_product_id,
        title: category.print_display_name || category.display_name || `${category.category} prints`,
      });
    });

    return lookup;
  }, [categories]);

  useEffect(() => {
    dispatch(getArtworks());
    dispatch(fetchArtworkCategories());
    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }
    if (categoriesError) {
      toast.error(categoriesMessage || 'Unable to load artwork categories');
    }
  }, [isError, message, categoriesError, categoriesMessage]);

  if (isLoading || categoriesLoading) {
    return <Loading />;
  }
  
  return (
    <div className='home-container'>
      <div
        className="home-landing"
        style={{
          '--home-bg': `url(${background})`,
          '--home-bg-mobile': `url(${background})`,
        }}
      >
        <Link to={'shop'}>
          <h1 className="home-title">Liiviiart</h1>
        </Link>
        {/* <Link to={'shop'}>
          <h2 className="home-subtitle">Christmas shop</h2>
        </Link> */}
      </div>
      
      <div className="portfolio-section">
        {homeCategories.length === 0 && (
          <p style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--color-ink-muted, #6b7280)' }}>
            No categories have been selected for the home page yet.
          </p>
        )}
        {homeCategories.map((category) => (
          <PortfolioSection
            key={category.category}
            title={category.display_name}
            artworks={artworksByCategory.get(category.category) || []}
            printCatalog={printCatalog}
          />
        ))}
      </div>
    </div>
  )
}

export default Home;
